import React, {useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import styled from 'styled-components';
import {sortBy} from 'lodash';
import {Button} from '@forecast-it/design-system';
import MissionControlHeader from '../type-script-files/MissionControlHeader';
import Util from '../../../forecast-app/shared/util/util';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {TopUpModal} from './top_up_modal';
import {formatPack} from './format_pack';

const AiCreditsStyle = styled.div`
	display: flex;
	flex-direction: column;
	max-height: 100vh;

	> .mission-control-list {
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
		min-height: 0;
	}
`;

const TableHeader = styled.thead`
	th {
		background-color: white;
		position: sticky;
		top: 0;
	}
`;

const cellStyle = {verticalAlign: 'middle', whiteSpace: 'nowrap'};

const AiCredits = props => {
	const {
		viewer: {
			missionControlData: {companyStats},
		},
	} = props;

	const [searchValue, setSearchValue] = useState('');
	const [selectedCompany, setSelectedCompany] = useState(null);

	const filteredCompanies = sortBy(
		(companyStats || []).filter(
			company => (company.companyName || '').toLowerCase().indexOf(searchValue.toLowerCase()) > -1
		),
		['companyId']
	);

	const renderAction = company => {
		if (company.aiCreditTopUpAllowed === true) {
			return (
				<Button emphasis={'medium'} onPress={() => setSelectedCompany(company)}>
					Top up
				</Button>
			);
		}
		if (company.aiCreditPack && company.tier === 'TRIAL') {
			return 'Trial';
		}
		return null;
	};

	return (
		<AiCreditsStyle>
			<div className="mission-control-list">
				<MissionControlHeader viewer={props.viewer} />
				<h1>AI credits</h1>
				<div>
					<input
						type="search"
						placeholder="Search"
						value={searchValue}
						onChange={event => setSearchValue(event.target.value)}
					/>
				</div>
				<div style={{flex: 1, minHeight: 0, overflow: 'auto'}}>
					<table>
						<TableHeader>
							<tr>
								<th>Company</th>
								<th>Company ID</th>
								<th>Tier</th>
								<th style={{whiteSpace: 'nowrap'}}>Core + Collaborator seats</th>
								<th>Pack</th>
								<th>&nbsp;</th>
							</tr>
						</TableHeader>
						<tbody>
							{filteredCompanies.map(company => (
								<tr key={company.id}>
									<td style={cellStyle}>{company.companyName}</td>
									<td style={cellStyle}>{company.companyId}</td>
									<td style={cellStyle}>{Util.getTiersMessage(company.tier)}</td>
									<td style={cellStyle}>{company.aiCreditSeats}</td>
									<td style={cellStyle}>{formatPack(company.aiCreditPack, company.aiCreditPackCredits)}</td>
									<td style={cellStyle}>{renderAction(company)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{selectedCompany ? (
					<TopUpModal
						company={selectedCompany}
						closeModal={() => setSelectedCompany(null)}
						onSuccess={payload =>
							createToast({
								duration: 5000,
								message: `Topped up ${selectedCompany.companyName} with ${formatPack(
									payload.pack,
									payload.credits
								)}`,
							})
						}
					/>
				) : null}
			</div>
		</AiCreditsStyle>
	);
};

const aiCreditsQuery = graphql`
	query aiCredits_Query {
		viewer {
			actualPersonId
			component(name: "control_ai_credits")
			...aiCredits_viewer
		}
	}
`;

export {aiCreditsQuery};

export default createFragmentContainer(AiCredits, {
	viewer: graphql`
		fragment aiCredits_viewer on Viewer {
			id
			...MissionControlHeader_viewer
			missionControlData {
				id
				companyStats {
					id
					companyId
					companyName
					tier
					trialEndDate
					aiCreditSeats
					aiCreditPack
					aiCreditPackCredits
					aiCreditTopUpAllowed
				}
			}
		}
	`,
});
